import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Box,
  Popper,
  Alert,
  Typography,
  CircularProgress,
  Chip
} from '@mui/material';
import { usePrices } from './hooks/usePrices';
import { useCoins } from './contexts/CoinsContext';
import TokenIcon from './components/TokenIcon';
import debounce from 'lodash.debounce';
import api from './services/api';
import { useTranslation } from 'react-i18next';

const now = new Date();
const pad = n => n.toString().padStart(2, '0');
const defaultInitialValues = {
  date: now.toISOString().slice(0, 10),
  hour: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
  type: 'Buy',
  coin: 'BTC',
  blockchain: '',
  shares: '',
  costPerShare: '',
  fee: '0'
};

// Custom Popper component that renders at body level
const CustomPopper = React.forwardRef((props, ref) => {
  return (
    <Popper
      {...props}
      ref={ref}
      style={{ zIndex: 99999 }}
      container={() => document.body}
    />
  );
});

export default function AddTransactionModal({
  open,
  onClose,
  onSubmit,
  loading = false,
  initialValues = {},
  error = null,
  formOnly = false,
  submitButtonText = "Add Transaction",
  editing = false
}) {
  const { t } = useTranslation();
  const { coins, loading: coinsLoading, refreshCoins } = useCoins();
  
  const coinSymbols = useMemo(() => {
    return Array.isArray(coins) ? coins.map(coin => coin.symbol).filter(Boolean) : [];
  }, [coins]);
  
  const { prices: currentPrices, refresh: refreshPrices } = usePrices(coinSymbols, false, 'AddTransactionModal');
  const [form, setForm] = useState({ ...defaultInitialValues, ...initialValues });
  const [touched, setTouched] = useState({});
  const isInitializing = useRef(false);
  const [wasOpen, setWasOpen] = useState(false);

  // Filter out coins that don't have a blockchain match
  const filteredCoins = useMemo(() => {
    if (!coins) return [];
    // Always show all coins, sorted by name
    return coins.sort((a, b) => a.name.localeCompare(b.name));
  }, [coins]);

  useEffect(() => {
    // Reset form when modal is opened or when initialValues change
    if (open) {
      isInitializing.current = true;
      // Reset form to default values or initialValues if provided
      const formValues = { ...defaultInitialValues, ...initialValues };
      
      // Handle buy_price field - if provided, use it for costPerShare
      if (initialValues.buy_price !== undefined) {
        formValues.costPerShare = initialValues.buy_price;
      } else if (initialValues.price !== undefined) {
        formValues.costPerShare = initialValues.price;
      }
      
      setForm(formValues);
      setTouched({});
      isInitializing.current = false;
    }
    setWasOpen(open);
  }, [open, initialValues]);

  // Reset wasOpen when modal is closed
  useEffect(() => {
    if (!open) {
      setWasOpen(false);
    }
  }, [open]);

  const handleChange = useCallback((field, value) => {
    // For numeric fields, parse the formatted value before storing
    let processedValue = value;
    if (field === 'shares' || field === 'costPerShare' || field === 'fee') {
      processedValue = parseFormattedNumber(value);
    }
    
    setForm((prev) => ({ ...prev, [field]: processedValue }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    
    // Auto-fill cost per share when coin is selected
    if (field === 'coin' && value) {
      const priceData = currentPrices[value];
      const currentPrice = priceData?.usd ?? 0;
      // console.log('[AddTransactionModal] Selected coin:', value, 'currentPrices:', currentPrices, 'currentPrice:', currentPrice);
      if (currentPrice && currentPrice > 0) {
        setForm((prev) => ({ ...prev, costPerShare: currentPrice.toString() }));
      } else {
        // Clear the cost per share if no price is available
        setForm((prev) => ({ ...prev, costPerShare: '' }));
        // Try to refresh prices to get the latest data
        refreshPrices();
      }
      
      // Auto-set blockchain when coin is selected
      const selectedCoin = filteredCoins.find(c => c.symbol === value);
      if (selectedCoin && selectedCoin.blockchain) {
        setForm((prev) => ({ ...prev, blockchain: selectedCoin.blockchain }));
      }
    }
  }, [currentPrices, refreshPrices, filteredCoins]);

  // Only auto-fill if there is no costPerShare (i.e., when adding, not editing)
  useEffect(() => {
    if (form.coin && (!form.costPerShare || Number(form.costPerShare) === 0)) {
      const priceData = currentPrices[form.coin];
      const currentPrice = priceData?.usd ?? 0;
      // console.log('[AddTransactionModal][useEffect] coin:', form.coin, 'currentPrices:', currentPrices, 'currentPrice:', currentPrice);
      if (currentPrice && currentPrice > 0) {
        setForm((prev) => ({ ...prev, costPerShare: currentPrice.toString() }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.coin, currentPrices]);

  // Helper function to format numbers with commas for display only
  const formatNumberForDisplay = (value) => {
    if (!value) return '';
    // If value ends with a dot or has multiple dots, return as is (user is typing a decimal)
    if (/\.$/.test(value) || (value.match(/\./g) || []).length > 1) return value;
    // If value contains a dot, don't format with commas (user is typing a decimal)
    if (value.includes('.')) return value;
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString();
  };

  // Helper function to parse formatted numbers
  const parseFormattedNumber = (value) => {
    if (!value) return '';
    return value.replace(/,/g, '');
  };

  const validate = () => {
    const sharesNum = parseFloat(parseFormattedNumber(form.shares));
    const costPerShareNum = parseFloat(parseFormattedNumber(form.costPerShare));
    
    return (
      form.date &&
      form.hour &&
      form.type &&
      form.coin &&
      form.shares &&
      !isNaN(sharesNum) &&
      sharesNum > 0 &&
      form.costPerShare &&
      !isNaN(costPerShareNum) &&
      costPerShareNum > 0
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...form,
        amount: Number(parseFormattedNumber(form.shares)),
        buy_price: Number(parseFormattedNumber(form.costPerShare)),
        price: Number(parseFormattedNumber(form.costPerShare)),
        shares: Number(parseFormattedNumber(form.shares)),
        costPerShare: Number(parseFormattedNumber(form.costPerShare)),
        fee: Number(form.fee) || 0,
        hour: form.hour,
        blockchain: form.blockchain
      });
    } else {
      setTouched({ date: true, hour: true, type: true, coin: true, shares: true, costPerShare: true });
    }
  };

  // --- Add Missing Coin UI state ---
  const [missingCoinQuery, setMissingCoinQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addCoinLoading, setAddCoinLoading] = useState(false);
  const [addCoinError, setAddCoinError] = useState(null);

  // --- Debounced search for coins by symbol/name ---
  const doSearch = useRef(
    debounce(async (query) => {
      if (query.length < 2) {
        setSearchResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      setAddCoinError(null);
      setSearchResults([]);
      try {
        const resp = await fetch(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(query)}`);
        const data = await resp.json();
        setSearchResults(data.coins || []);
      } catch (err) {
        setAddCoinError('Failed to search for coins.');
      } finally {
        setSearching(false);
      }
    }, 1000)
  ).current;

  useEffect(() => {
    if (missingCoinQuery.length >= 2) {
      doSearch(missingCoinQuery);
    } else {
      setSearchResults([]);
      setSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingCoinQuery]);

  // --- Add missing coin to backend ---
  const handleAddMissingCoin = async (coin) => {
    setAddCoinLoading(true);
    setAddCoinError(null);
    try {
      const resp = await api.post('/api/coins/add', {
        symbol: coin.symbol.toUpperCase(),
        name: coin.name,
        coingecko_id: coin.id,
        image_url: coin.large,
        blockchain: 'ethereum' // Default, or let user pick in future
      });
      const result = resp.data;
      if (!result.success) throw new Error(result.error || 'Failed to add coin');
      // Refresh the coins list
      await refreshCoins();
      // Select the new coin in the dropdown
      setForm((prev) => ({ ...prev, coin: coin.symbol.toUpperCase() }));
      // Clear search results and input
      setSearchResults([]);
      setMissingCoinQuery('');
    } catch (err) {
      setAddCoinError(err.message || 'Failed to add coin.');
    } finally {
      setAddCoinLoading(false);
    }
  };

  // Render only the form if formOnly is true
  if (formOnly) {
    return (
      <form onSubmit={handleSubmit} style={{ minHeight: 320, paddingTop: 16, paddingBottom: 16 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('common.date')}
              type="date"
              value={form.date}
              onChange={e => handleChange('date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              error={Boolean(touched.date && !form.date)}
              helperText={touched.date && !form.date ? t('common.pleaseSelectDate') : ''}
              sx={{ flex: 1 }}
            />
            <TextField
              label={t('common.hour')}
              type="time"
              value={form.hour}
              onChange={e => handleChange('hour', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
                              error={Boolean(touched.hour && !form.hour)}
              helperText={touched.hour && !form.hour ? t('common.pleaseSelectHour') : ''}
              sx={{ flex: 1 }}
            />
          </Box>
          <FormControl fullWidth required error={Boolean(touched.type && !form.type)}>
            <InputLabel>{t('common.transactionType')}</InputLabel>
            <Select
              value={form.type}
              label={t('common.transactionType')}
              onChange={e => handleChange('type', e.target.value)}
            >
              <MenuItem value="Buy">{t('common.buy')}</MenuItem>
              <MenuItem value="Sell">{t('common.sell')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth required error={Boolean(touched.coin && !form.coin)}>
            <InputLabel>{t('common.coin')}</InputLabel>
            <Select
              value={form.coin}
              label={t('common.coin')}
              onChange={(e) => handleChange('coin', e.target.value)}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    zIndex: 99999
                  }
                }
              }}
              disabled={editing}
            >
              {filteredCoins.map(coin => (
                <MenuItem key={coin.symbol} value={coin.symbol}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <TokenIcon coinSymbol={coin.symbol} size={24} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <span>{coin.name} ({coin.symbol})</span>
                      {coin.blockchain && (
                        <span style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {coin.blockchain.toUpperCase()}
                        </span>
                      )}
                    </Box>
                    {coin.in_use && (
                      <Chip 
                        label={t('common.inUse')} 
                        size="small" 
                        color="success" 
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: 20 }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Add Missing Coin UI */}
          <Box sx={{ mt: 1, mb: 2, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
            <Typography variant="subtitle2">{t('common.addMissingCoin')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <TextField
                size="small"
                label={t('common.coinSymbolOrName')}
                value={missingCoinQuery}
                onChange={e => setMissingCoinQuery(e.target.value)}
                disabled={addCoinLoading}
                sx={{ flex: 1 }}
              />
            </Box>
            {addCoinError && <Alert severity="error" sx={{ mt: 1 }}>{addCoinError}</Alert>}
            {searchResults.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {searchResults.map(coin => (
                  <Box key={coin.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <img src={coin.large} alt={coin.symbol} width={24} height={24} style={{ borderRadius: 12 }} />
                    <span>{coin.name} ({coin.symbol.toUpperCase()})</span>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleAddMissingCoin(coin)}
                      disabled={addCoinLoading}
                    >
                      {addCoinLoading ? t('common.adding') : t('common.add')}
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <TextField
            label={t('common.amount')}
            value={formatNumberForDisplay(form.shares)}
            onChange={e => handleChange('shares', e.target.value)}
            required
            error={Boolean(touched.shares && (!form.shares || isNaN(parseFormattedNumber(form.shares))))}
            helperText={touched.shares && (!form.shares || isNaN(parseFormattedNumber(form.shares))) ? t('common.enterValidAmount') : ''}
            fullWidth
          />
          <TextField
            label={t('common.buyPricePerCoin')}
            value={formatNumberForDisplay(form.costPerShare)}
            onChange={e => handleChange('costPerShare', e.target.value)}
            required
            error={Boolean(touched.costPerShare && (!form.costPerShare || isNaN(parseFormattedNumber(form.costPerShare))))}
            helperText={touched.costPerShare && (!form.costPerShare || isNaN(parseFormattedNumber(form.costPerShare))) ? t('common.enterValidPrice') : ''}
            fullWidth
          />
          <TextField
            label={t('common.fee')}
            value={form.fee}
            onChange={e => handleChange('fee', e.target.value)}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
          <Button onClick={onClose} color="secondary" variant="outlined" sx={{ minWidth: 100 }}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 100 }}>{submitButtonText}</Button>
        </Box>
      </form>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('common.addTransaction')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('common.date')}
                type="date"
                value={form.date}
                onChange={e => handleChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={Boolean(touched.date && !form.date)}
                helperText={touched.date && !form.date ? t('common.pleaseSelectDate') : ''}
                sx={{ flex: 1 }}
              />
              <TextField
                label={t('common.hour')}
                type="time"
                value={form.hour}
                onChange={e => handleChange('hour', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={Boolean(touched.hour && !form.hour)}
                helperText={touched.hour && !form.hour ? t('common.pleaseSelectHour') : ''}
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControl fullWidth required error={Boolean(touched.type && !form.type)}>
              <InputLabel>{t('common.transactionType')}</InputLabel>
              <Select
                value={form.type}
                label={t('common.transactionType')}
                onChange={e => handleChange('type', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      zIndex: 99999
                    }
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  }
                }}
              >
                <MenuItem value="Buy">{t('common.buy')}</MenuItem>
                <MenuItem value="Sell">{t('common.sell')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth required error={Boolean(touched.coin && !form.coin)}>
              <InputLabel>{t('common.coin')}</InputLabel>
              <Select
                value={form.coin}
                label={t('common.coin')}
                onChange={(e) => handleChange('coin', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      zIndex: 99999
                    }
                  }
                }}
                disabled={editing}
              >
                {filteredCoins.map(coin => (
                  <MenuItem key={coin.symbol} value={coin.symbol}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <TokenIcon coinSymbol={coin.symbol} size={24} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <span>{coin.name} ({coin.symbol})</span>
                        {coin.blockchain && (
                          <span style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {coin.blockchain.toUpperCase()}
                          </span>
                        )}
                      </Box>
                      {coin.in_use && (
                        <Chip 
                          label={t('common.inUse')} 
                          size="small" 
                          color="success" 
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 20 }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Add Missing Coin UI */}
            <Box sx={{ mt: 1, mb: 2, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
              <Typography variant="subtitle2">{t('common.addMissingCoin')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <TextField
                  size="small"
                  label={t('common.coinSymbolOrName')}
                  value={missingCoinQuery}
                  onChange={e => setMissingCoinQuery(e.target.value)}
                  disabled={addCoinLoading}
                  sx={{ flex: 1 }}
                />
              </Box>
              {addCoinError && <Alert severity="error" sx={{ mt: 1 }}>{addCoinError}</Alert>}
              {searchResults.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {searchResults.map(coin => (
                    <Box
                      key={coin.id}
                      sx={{
                        mb: 1,
                        p: 1,
                        borderRadius: 1,
                        transition: 'background 0.2s',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <img
                          src={coin.large}
                          alt={coin.symbol}
                          width={28}
                          height={28}
                          style={{ borderRadius: 14, flexShrink: 0, marginRight: 8 }}
                        />
                        <Typography
                          variant="body1"
                          noWrap
                          sx={{ fontWeight: 500, minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {coin.name} ({coin.symbol.toUpperCase()})
                        </Typography>
                        <Button
                          sx={{ ml: 'auto' }}
                          variant="contained"
                          size="small"
                          onClick={e => { e.stopPropagation(); handleAddMissingCoin(coin); }}
                          disabled={addCoinLoading}
                        >
                          {addCoinLoading ? t('common.adding') : t('common.add')}
                        </Button>
                        {addCoinLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <TextField
              label={t('common.shares')}
              type="text"
              fullWidth
              value={formatNumberForDisplay(form.shares)}
              onChange={e => handleChange('shares', parseFormattedNumber(e.target.value))}
              required
              error={Boolean(touched.shares && (!form.shares || isNaN(Number(form.shares)) || Number(form.shares) <= 0))}
              helperText={
                touched.shares && (!form.shares || isNaN(Number(form.shares)) || Number(form.shares) <= 0)
                  ? t('common.pleaseEnterValidNumberOfShares')
                  : ''
              }
            />
            <TextField
              label={t('common.buyPricePerCoin')}
              value={formatNumberForDisplay(form.costPerShare)}
              onChange={e => handleChange('costPerShare', parseFormattedNumber(e.target.value))}
              required
              error={Boolean(touched.costPerShare && (!form.costPerShare || isNaN(Number(form.costPerShare)) || Number(form.costPerShare) <= 0))}
              helperText={
                touched.costPerShare && (!form.costPerShare || isNaN(Number(form.costPerShare)) || Number(form.costPerShare) <= 0)
                  ? t('common.pleaseEnterValidBuyPricePerCoin')
                  : ''
              }
            />
            <TextField
              label={t('common.fee')}
              type="text"
              fullWidth
              value={form.fee}
              onChange={e => handleChange('fee', e.target.value)}
              error={Boolean(touched.fee && (form.fee && (isNaN(Number(form.fee)) || Number(form.fee) < 0)))}
              helperText={
                touched.fee && (form.fee && (isNaN(Number(form.fee)) || Number(form.fee) < 0))
                  ? t('common.pleaseEnterValidFeeAmount')
                  : t('common.optionalEnterTransactionFees')
              }
            />
          </Box>
          {/* Live Value Calculation */}
          <Box sx={{ mt: 2, textAlign: 'left' }}>
            {(() => {
              const sharesNum = parseFloat(parseFormattedNumber(form.shares));
              const costNum = parseFloat(parseFormattedNumber(form.costPerShare));
              const value = (!isNaN(sharesNum) && !isNaN(costNum)) ? sharesNum * costNum : '';
              return (
                <span style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {t('common.value')}: {value === '' ? '-' : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!validate()}
          >
            {submitButtonText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
} 